import React, { useState, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts } from "@/constants/ThemeColours";

// Safely obtain createPortal from react-dom when on web
let createPortal: ((children: React.ReactNode, container: Element) => React.ReactPortal) | null = null;
if (Platform.OS === "web" && typeof document !== "undefined") {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    createPortal = require("react-dom").createPortal;
  } catch {
    createPortal = null;
  }
}

/**
 * Trigger rectangle metrics for fixed positioning.
 */
interface ITriggerRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Props for ThemedToolTip component.
 */
export interface IThemedToolTipProps {
  /** Text content displayed in the tooltip */
  text: string;
  /** The child component wrapped by the tooltip trigger */
  children: React.ReactNode;
  /** Preferred tooltip popover position (default: "top") */
  position?: "top" | "bottom" | "left" | "right";
  /** Whether the tooltip is disabled */
  disabled?: boolean;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
}

/**
 * Standard themed tooltip component adhering to Memories UX Standards:
 * zero-radius square corners, token colors, and unified hover handling matching buttons.
 *
 * Mounts into document.body portal on Web to ensure tooltips reliably appear
 * above all other controls without being clipped by parent overflow: hidden or z-index stacking contexts.
 */
export function ThemedToolTip({
  text,
  children,
  position = "top",
  disabled = false,
  style,
}: IThemedToolTipProps): React.JSX.Element {
  const [visible, setVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState<ITriggerRect | null>(null);
  const containerRef = useRef<View>(null);
  const timeoutRef = useRef<any>(null);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");

  const updateRect = (e?: any) => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      let rect: DOMRect | null = null;
      if (e?.currentTarget && typeof e.currentTarget.getBoundingClientRect === "function") {
        rect = e.currentTarget.getBoundingClientRect();
      } else if (containerRef.current) {
        const node = containerRef.current as any;
        if (typeof node.getBoundingClientRect === "function") {
          rect = node.getBoundingClientRect();
        }
      }
      if (rect) {
        setTriggerRect({
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
          width: rect.width,
          height: rect.height,
        });
      }
    }
  };

  const handleMouseEnter = (e?: any) => {
    if (disabled || !text) return;
    updateRect(e);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      updateRect();
      setVisible(true);
    }, 120);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setVisible(false);
  };

  // Automatically dismiss tooltip when window is scrolled or resized
  useEffect(() => {
    if (!visible || Platform.OS !== "web" || typeof window === "undefined") return;

    const handleDismiss = () => {
      setVisible(false);
    };

    window.addEventListener("scroll", handleDismiss, true);
    window.addEventListener("resize", handleDismiss, true);

    return () => {
      window.removeEventListener("scroll", handleDismiss, true);
      window.removeEventListener("resize", handleDismiss, true);
    };
  }, [visible]);

  // Clean up any pending timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Compute portal coordinates when mounted in body
  const renderPortalTooltip = () => {
    if (!triggerRect || typeof window === "undefined" || typeof document === "undefined" || !createPortal) {
      return null;
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    let effPos = position;
    if (effPos === "top" && triggerRect.top < 36) {
      effPos = "bottom";
    } else if (effPos === "bottom" && triggerRect.bottom + 36 > screenHeight) {
      effPos = "top";
    } else if (effPos === "left" && triggerRect.left < 80) {
      effPos = "right";
    } else if (effPos === "right" && triggerRect.right + 80 > screenWidth) {
      effPos = "left";
    }

    let top = 0;
    let left = 0;
    let transform = "";

    if (effPos === "top") {
      top = triggerRect.top - 6;
      left = triggerRect.left + triggerRect.width / 2;
      transform = "translate(-50%, -100%)";
    } else if (effPos === "bottom") {
      top = triggerRect.bottom + 6;
      left = triggerRect.left + triggerRect.width / 2;
      transform = "translate(-50%, 0)";
    } else if (effPos === "left") {
      top = triggerRect.top + triggerRect.height / 2;
      left = triggerRect.left - 6;
      transform = "translate(-100%, -50%)";
    } else if (effPos === "right") {
      top = triggerRect.top + triggerRect.height / 2;
      left = triggerRect.right + 6;
      transform = "translate(0, -50%)";
    }

    left = Math.max(16, Math.min(screenWidth - 16, left));

    const portalContent = (
      <View
        pointerEvents="none"
        style={[
          styles.tooltipBubblePortal,
          {
            backgroundColor: cardBg,
            borderColor,
            top,
            left,
            transform: transform as any,
          },
        ]}
      >
        <Text style={[styles.tooltipText, { color: textColor }]}>
          {text}
        </Text>
      </View>
    );

    return createPortal(portalContent, document.body);
  };

  const canUsePortal = Platform.OS === "web" && typeof document !== "undefined" && Boolean(createPortal) && Boolean(triggerRect);

  return (
    <View
      ref={containerRef}
      style={[styles.container, visible && { zIndex: 99999 }, style]}
      {...(Platform.OS === "web"
        ? {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          }
        : {})}
    >
      {children}

      {visible && !disabled && !!text && (
        canUsePortal ? (
          renderPortalTooltip()
        ) : (
          <View
            pointerEvents="none"
            style={[
              styles.tooltipBubble,
              position === "top" && styles.bubbleTop,
              position === "bottom" && styles.bubbleBottom,
              position === "left" && styles.bubbleLeft,
              position === "right" && styles.bubbleRight,
              {
                backgroundColor: cardBg,
                borderColor,
              },
            ]}
          >
            <Text style={[styles.tooltipText, { color: textColor }]}>
              {text}
            </Text>
          </View>
        )
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  tooltipBubble: {
    position: "absolute",
    zIndex: 99999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    borderWidth: 1,
    minWidth: 40,
    maxWidth: 240,
    elevation: 6,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.15)",
          whiteSpace: "nowrap",
          userSelect: "none",
        } as any)
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.15,
          shadowRadius: 4,
        }),
  },
  tooltipBubblePortal: {
    position: "fixed" as any,
    zIndex: 10000000,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 0,
    borderWidth: 1,
    minWidth: 40,
    maxWidth: 260,
    elevation: 10,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
          whiteSpace: "nowrap",
          userSelect: "none",
        } as any)
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.18,
          shadowRadius: 4,
        }),
  },
  bubbleTop: {
    bottom: "100%",
    marginBottom: 6,
    alignSelf: "center",
  },
  bubbleBottom: {
    top: "100%",
    marginTop: 6,
    alignSelf: "center",
  },
  bubbleLeft: {
    right: "100%",
    marginRight: 6,
  },
  bubbleRight: {
    left: "100%",
    marginLeft: 6,
  },
  tooltipText: {
    fontSize: 11,
    fontFamily: Fonts.mono,
    textAlign: "center",
    lineHeight: 14,
  },
});
